import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { BRAND } from '@/components/proepoxy/productData';
import { loadProEpoxyCatalogue } from '@/components/proepoxy/catalogueLoader';
import SystemSelection from '@/components/proepoxy/SystemSelection';
import MetallicEpoxyFlow from '@/components/proepoxy/MetallicEpoxyFlow';
import HighBuildEpoxyFlow from '@/components/proepoxy/HighBuildEpoxyFlow';

const ProEpoxyOrderBuilder = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEmbedMode = searchParams.get('embed') === 'true';
  const { user, isAuthenticated } = useAuth();

  const [selectedSystem, setSelectedSystem] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [highBuildOrderMode, setHighBuildOrderMode] = useState(null);
  // Live pack sizes/prices from staff catalogue — must land before any step
  // runs its pack maths. Falls back to built-in defaults if the fetch fails.
  const [catalogueReady, setCatalogueReady] = useState(false);
  useEffect(() => {
    loadProEpoxyCatalogue({ force: true }).catch(() => {}).finally(() => setCatalogueReady(true));
  }, []);

  const goToStep = (step) => {
    setCurrentStep(step);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSystemSelect = (systemId) => {
    setSelectedSystem(systemId);
    setCurrentStep(1);
  };

  const handleExit = () => {
    setSelectedSystem(null);
    setHighBuildOrderMode(null);
    goToStep(0);
  };

  const handleBack = () => {
    if (currentStep === 0) {
      navigate(-1);
    } else if (currentStep === 1) {
      if (selectedSystem === 'high_build' && highBuildOrderMode) {
        setHighBuildOrderMode(null);
      } else {
        handleExit();
      }
    } else {
      goToStep(currentStep - 1);
    }
  };

  return (
    <div className="min-h-screen bg-[#E8ECEF]">
      {!isEmbedMode && (
        <header className="bg-white shadow-sm" style={{ borderBottom: `4px solid ${BRAND.primaryColor}` }}>
          <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
            <Button
              onClick={handleBack}
              variant="outline"
              className="border-2 text-gray-600 hover:bg-gray-100"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-4">
              <img
                src={BRAND.logo}
                alt={BRAND.fullName}
                className="h-12 md:h-14"
              />
            </div>
          </div>
        </header>
      )}

      <main className="max-w-4xl mx-auto px-4 py-8">
        {!catalogueReady && (
          <div className="text-center text-gray-500 py-16" data-testid="proepoxy-catalogue-loading">Loading current pack sizes &amp; prices…</div>
        )}
        {catalogueReady && currentStep === 0 && (
          <SystemSelection onSelect={handleSystemSelect} />
        )}

        {catalogueReady && selectedSystem === 'metallic' && currentStep > 0 && (
          <MetallicEpoxyFlow
            currentStep={currentStep}
            goToStep={goToStep}
            onExit={handleExit}
          />
        )}

        {catalogueReady && selectedSystem === 'high_build' && currentStep > 0 && (
          <HighBuildEpoxyFlow
            currentStep={currentStep}
            goToStep={goToStep}
            onExit={handleExit}
            highBuildOrderMode={highBuildOrderMode}
            setHighBuildOrderMode={setHighBuildOrderMode}
          />
        )}
      </main>
    </div>
  );
};

export default ProEpoxyOrderBuilder;
